import { useState, cloneElement } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Lock, CreditCard, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { trackEvent } from "@/lib/firebase";
import { toast } from "sonner";

export const PayCourseDialog = ({ trigger, course, courseId, priceINR }) => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("confirm"); // confirm | processing | success | failed
  const [orderMode, setOrderMode] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const finalizeEnrollment = async (orderId) => {
    // Backend now auto-enrolls inside /payments/verify. We keep this as a
    // best-effort fallback for edge cases (mocked mode, or if backend enroll
    // failed silently). Errors here are non-fatal — refresh user + move on.
    if (!courseId) return;
    try {
      await api.post("/courses/enroll", { course_id: courseId, order_id: orderId, source: "checkout" });
    } catch (e) {
      // Already enrolled (via backend auto-enroll) or transient error — log only.
      console.debug("[Payment] finalizeEnrollment skipped/failed", e?.response?.data || e);
    }
    refreshUser?.();
  };

  const start = async () => {
    if (!user) { setOpen(false); navigate("/login"); return; }
    setStep("processing");
    setErrorMsg("");
    try {
      const { data } = await api.post("/payments/order", {
        amount: priceINR * 100, currency: "INR", course, course_id: courseId,
      });
      setOrderMode(data.mode);
      if (data.mode === "live") {
        // 🔧 Close the outer Radix Dialog BEFORE Razorpay opens.
        // Radix's focus-trap + `onInteractOutside` fight with Razorpay's
        // iframe — users otherwise can't type into the phone field or
        // dismiss the checkout. We reopen the dialog in each callback so
        // success / failed / dismiss states still surface.
        setOpen(false);
        await openRazorpayCheckout({
          keyId: data.key_id,
          amount: data.amount,
          currency: data.currency,
          orderId: data.order_id,
          name: "Language Nest India",
          description: course,
          prefill: (() => {
            const phone = (user?.phone || "").replace(/\D/g, "").slice(-10);
            const p = {
              name: user?.name || "",
              email: user?.email || "",
            };
            if (phone.length === 10) p.contact = phone;
            return p;
          })(),
          onSuccess: async (res) => {
            setOpen(true);
            setStep("processing");
            try {
              await api.post("/payments/verify", {
                order_id: data.order_id,
                payment_id: res.razorpay_payment_id,
                signature: res.razorpay_signature,
              });
              await finalizeEnrollment(data.order_id);
              trackEvent("purchase", { currency: "INR", value: priceINR, items: [{ item_name: course, item_id: courseId }] });
              setStep("success");
              toast.success(`Payment confirmed — you're enrolled in ${course}!`);
            } catch (err) {
              const msg = formatApiErrorDetail(err.response?.data?.detail) || "Payment verification failed";
              setErrorMsg(msg);
              setStep("failed");
              toast.error(msg);
            }
          },
          onFailure: (info) => {
            setOpen(true);
            const msg = `${info.description} (${info.code})`;
            setErrorMsg(msg);
            setStep("failed");
            toast.error(`Payment failed: ${msg}`);
          },
          onDismiss: () => {
            // UPI intent flow: user may have completed payment in Google Pay
            // then closed Razorpay before the JS handler fired. Poll the backend
            // for 30 seconds — the backend asks Razorpay directly.
            setOpen(true);
            setStep("processing");
            toast.info("Checking payment status — please wait a moment…");
            let elapsed = 0;
            const iv = setInterval(async () => {
              elapsed += 3;
              try {
                const { data: st } = await api.get(`/payments/status/${data.order_id}`);
                if (st.status === "paid") {
                  clearInterval(iv);
                  await finalizeEnrollment(data.order_id);
                  trackEvent("purchase", { currency: "INR", value: priceINR, items: [{ item_name: course, item_id: courseId }] });
                  setStep("success");
                  toast.success(`Payment confirmed — you're enrolled in ${course}!`);
                  return;
                }
                if (st.status === "failed" || st.status === "cancelled") {
                  clearInterval(iv);
                  setErrorMsg("Payment was not completed. No amount will be charged.");
                  setStep("failed");
                  return;
                }
                if (elapsed >= 30) {
                  clearInterval(iv);
                  setStep("confirm");
                  toast.info("Still processing on Razorpay's side — refresh in a minute or contact support if your account was debited.");
                }
              } catch {
                if (elapsed >= 30) {
                  clearInterval(iv);
                  setStep("confirm");
                }
              }
            }, 3000);
          },
        });
        return;
      }
      // MOCK mode: auto-verify
      const verify = await api.post("/payments/verify", { order_id: data.order_id });
      if (verify.data.ok) {
        await finalizeEnrollment(data.order_id);
        trackEvent("purchase", { currency: "INR", value: priceINR, items: [{ item_name: course, item_id: courseId }] });
        setStep("success");
      } else {
        setErrorMsg("Payment verification failed");
        setStep("failed");
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Payment failed";
      setErrorMsg(msg);
      setStep("failed");
      toast.error(msg);
    }
  };

  const wrapped = cloneElement(trigger, { onClick: () => { setStep("confirm"); setErrorMsg(""); setOpen(true); } });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{wrapped}</DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="pay-course-dialog">
        {step === "confirm" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-brand-navy">Enroll in {course}</DialogTitle>
              <DialogDescription>Secure payment via Razorpay. Cancel anytime before the batch starts.</DialogDescription>
            </DialogHeader>
            <div className="my-4 rounded-2xl bg-brand-muted-bg p-5 flex items-center justify-between">
              <div className="text-brand-navy font-semibold">{course}</div>
              <div className="font-heading font-black text-3xl text-brand-red">₹{priceINR.toLocaleString("en-IN")}</div>
            </div>
            <Button data-testid="pay-confirm-btn" onClick={start} className="w-full h-12 rounded-full bg-brand-red hover:bg-brand-red-hover font-semibold text-white">
              <CreditCard className="w-4 h-4 mr-2" /> Pay securely
            </Button>
            <div className="mt-3 text-xs text-brand-navy/50 flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> 256-bit SSL · Powered by Razorpay</div>
          </>
        )}
        {step === "processing" && (
          <div className="py-10 text-center" data-testid="pay-processing">
            <Loader2 className="w-10 h-10 animate-spin text-brand-red mx-auto" />
            <div className="mt-4 font-heading text-lg text-brand-navy">Confirming payment…</div>
            <div className="mt-1 text-sm text-brand-navy/50">Please don&#39;t close this window.</div>
            <Button
              variant="outline"
              onClick={() => { setStep("confirm"); setOpen(false); }}
              data-testid="pay-processing-cancel-btn"
              className="mt-5 rounded-full"
            >
              Cancel
            </Button>
          </div>
        )}
        {step === "success" && (
          <div className="py-6 text-center" data-testid="pay-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <div className="mt-4 font-heading font-black text-2xl text-brand-navy">Payment confirmed</div>
            <div className="mt-2 text-brand-navy/60 text-sm">
              You&#39;re enrolled in <span className="font-semibold">{course}</span>. Our team will WhatsApp your batch link within an hour.
            </div>
            {orderMode === "mock" && (
              <div className="mt-3 text-[10px] uppercase tracking-widest text-brand-gold font-bold">MOCKED PAYMENT · No card charged</div>
            )}
            <div className="mt-6 flex gap-2 justify-center flex-wrap">
              {courseId && (
                <Button
                  data-testid="pay-goto-course-btn"
                  onClick={() => { setOpen(false); navigate(`/courses/${courseId}`); }}
                  className="rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold"
                >
                  Go to course
                </Button>
              )}
              <Button data-testid="pay-close-btn" variant="outline" onClick={() => setOpen(false)} className="rounded-full">
                Done
              </Button>
            </div>
          </div>
        )}
        {step === "failed" && (
          <div className="py-6 text-center" data-testid="pay-failed">
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <div className="mt-4 font-heading font-black text-2xl text-brand-navy">Couldn&#39;t confirm</div>
            <div className="mt-2 text-brand-navy/70 text-sm">
              {errorMsg || "The payment gateway didn't return a valid confirmation."}
              <br />No amount will be captured. Please try again.
            </div>
            <div className="mt-6 flex gap-2 justify-center">
              <Button data-testid="pay-retry-btn" onClick={() => setStep("confirm")} className="rounded-full bg-brand-red hover:bg-brand-red-hover text-white">Try again</Button>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
